import { useCallback, useEffect, useState } from 'react'

import {
  getExperienceSubmissionsStats,
  SubmissionEntry,
  SubmissionOutcome,
  SubmissionsCounters,
} from '../utils/helpers/submissions.helpers'

const EMPTY_COUNTERS: SubmissionsCounters = { total: 0, pending: 0, approved: 0, rejected: 0, revised: 0 }

/**
 * The proposal history of one experience, paged (12.2).
 *
 * The outcome filter is passed to the ENDPOINT, never applied to the loaded
 * rows: with a page limit, filtering client-side would show "3 approved" on a
 * page that held thirty. Changing the filter restarts from the first page; "show
 * more" appends the next one. `hasMore` comes from the counters — the total, or
 * the filtered outcome's count — so the UI never has to guess.
 */
export const useExperienceSubmissions = ({ experienceId }: { experienceId?: string }) => {
  const [outcome, setOutcome] = useState<SubmissionOutcome | null>(null)
  const [counters, setCounters] = useState<SubmissionsCounters>(EMPTY_COUNTERS)
  const [submissions, setSubmissions] = useState<SubmissionEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!experienceId) return

    let alive = true
    setIsLoading(true)
    setError(false)

    getExperienceSubmissionsStats({ experienceId, outcome, offset: 0 }).then((result) => {
      if (!alive) return
      setForbidden(result.forbidden)
      setError(result.error)
      setCounters(result.page?.counters ?? EMPTY_COUNTERS)
      setSubmissions(result.page?.submissions ?? [])
      setIsLoading(false)
    })

    return () => {
      alive = false
    }
  }, [experienceId, outcome])

  const expected = outcome ? counters[outcome] : counters.total
  const hasMore = submissions.length < expected

  const loadMore = useCallback(async () => {
    if (!experienceId || isLoadingMore || !hasMore) return

    setIsLoadingMore(true)
    const result = await getExperienceSubmissionsStats({ experienceId, outcome, offset: submissions.length })
    if (result.page) {
      setCounters(result.page.counters)
      setSubmissions((current) => [...current, ...result.page!.submissions])
    }
    setIsLoadingMore(false)
  }, [experienceId, outcome, submissions.length, isLoadingMore, hasMore])

  return {
    outcome,
    setOutcome,
    counters,
    submissions,
    isLoading,
    isLoadingMore,
    forbidden,
    error,
    hasMore,
    loadMore,
  }
}
